import React, { useState, useEffect, useRef } from 'react';
import Message from './Message';
import Spinner from '../Spinner';
import useApi from '../api';
import { MessageType } from '../types';
import AgentPanel from './AgentPanel';
import ChatInput from './ChatInput';
import { conLog } from './utils/conLog';
import useAnalytics from '../analytics/liveAnalytics';
import config from '../config.json';

interface AgentProps {
    agentId?: string;
    closeModal?: any;
    sidebar?: boolean;
    setSidebar?: any;
    embedConfig?: any;
    agentLinks?: any;
    lastConvId?: any;
    lastMessages?: MessageType[];
    agentStarters?: any;
    existLeadId?: string;
    visitorId?: string;
    welcomeMessage: string;
    allParams?: any;
    draftAgentId?: string;
}

const backendUrl = config.env === 'development' ? 'http://localhost:8000' : process.env.REACT_APP_API_URL as string;

const AgentChat = ({ agentId, closeModal, sidebar, setSidebar, embedConfig, agentLinks, agentStarters, lastConvId, lastMessages, existLeadId, visitorId, welcomeMessage, allParams, draftAgentId }: AgentProps) => {
    const [loading, setLoading] = useState(false);
    const [messages, setMessages] = useState<MessageType[]>(lastMessages ? lastMessages : []);
    const [conversationId, setConversationId] = useState(lastConvId);
    const [assistantResponse, setAssistantResponse] = useState('');
    const [leadId, setLeadId] = useState<string | undefined>(existLeadId);
    const [ipAddress, setIpAddress] = useState('');
    const [noLead, setNoLead] = useState(false);
    const [links, setLinks] = useState<any>(agentLinks);
    const [starters, setStarters] = useState<any>(agentStarters);
    const [followUps, setFollowUps] = useState<any>([]);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [logo, setLogo] = useState<any | undefined>();
    const [initMessageSent, setInitMessageSent] = useState(messages.filter((message) => message.role === 'user').length > 0);
    const [results, setResults] = useState<any>([]);
    const [config, setConfig] = useState<any | undefined>(embedConfig);
    const [gettingResponse, setGettingResponse] = useState(false);
    const [gettingFullResponse, setGettingFullResponse] = useState(false);
    const [shouldScroll, setShouldScroll] = useState(false);
    const { get, post, postStream, connectWebSocket } = useApi();

    const [firstName, setFirstName] = useState(allParams['first_name'] ? allParams['first_name'] : '');
    const [lastName, setLastName] = useState(allParams['last_name'] ? allParams['last_name'] : '');
    const [email, setEmail] = useState(allParams['aimdoc_email'] ? allParams['aimdoc_email'] : '');
    const [attributes, setAttributes] = useState<Record<string, string>>(allParams ? allParams : {});
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [company, setCompany] = useState(allParams['company'] ? allParams['company'] : '');
    const [jobTitle, setJobTitle] = useState(allParams['job_title'] ? allParams['job_title'] : '');
    const [showFollowUps, setShowFollowUps] = useState(false);
    const [agentName, setAgentName] = useState<string | undefined>(undefined);
    const [showAgentEnteredMessage, setShowAgentEnteredMessage] = useState(false);
    const [showAgentLeftMessage, setShowAgentLeftMessage] = useState(false);
    const [showConnectedMessage, setShowConnectedMessage] = useState(false);
    const [handBackChat, setHandBackChat] = useState(false);
    const [takeOverChat, setTakeOverChat] = useState(false);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const [isAgentTyping, setIsAgentTyping] = useState(false);
    const shouldReconnectRef = useRef(true);

    const { captureEvent } = useAnalytics();

    const socketRef = useRef<WebSocket | null>(null); // Ref to store WebSocket instance

    const chatRef = React.useRef<HTMLDivElement | null>(null);

    const messagesRef = React.useRef(messages);
    messagesRef.current = messages;

    const assistantResponseRef = React.useRef(assistantResponse);
    assistantResponseRef.current = assistantResponse;

    const conversationIdRef = React.useRef(conversationId);
    conversationIdRef.current = conversationId;

    const leadIdRef = React.useRef(leadId);
    leadIdRef.current = leadId;

    const scrollToBottom = () => {
        if (chatRef.current) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
        }
    };

    useEffect(() => {
        if (shouldScroll) {
            scrollToBottom();
            setShouldScroll(false);
        }
    }, [shouldScroll]);


    function hexToRgb(hex: string) {
        // Remove the hash at the start if it's there
        hex = hex.replace(/^#/, '');

        // Parse r, g, b values
        let bigint = parseInt(hex, 16);
        let r = (bigint >> 16) & 255;
        let g = (bigint >> 8) & 255;
        let b = bigint & 255;

        return `${r}, ${g}, ${b}`;
    }

    const consumeStream = async (stream: any) => {
        const reader = stream.getReader();
        let receivedText = ''; // To accumulate the converted text
        let conversationId = '';
        setLoading(false);
        scrollToBottom();
        try {
            let chunkText = ''; // To store the chunk text
            let chunkCounter = 0;
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                // Convert the Uint8Array to String
                const chunk = new TextDecoder().decode(value);
                chunkText += chunk;
                // Split the chunk text by newline character
                const chunks = chunkText.split('\n');
                // Process each chunk
                for (let i = 0; i < chunks.length - 1; i++) {
                    const chunk = chunks[i];
                    // Attempt to parse JSON text and handle the data
                    try {
                        const data = JSON.parse(chunk);
                        if (data.type === 'document') {
                            setResults(data.results);
                        }
                        if (data.type === 'assistant' && data.content && !data.final) {
                            setGettingResponse(false);
                            setAssistantResponse(receivedText);
                            receivedText += data.content;
                        }
                        if (data.conversation_id && !conversationId) {
                            conversationId = data.conversation_id;
                            setConversationId(conversationId);
                        } else {
                            captureEvent('null_conversation', {
                                agent_id: agentId,
                                user_id: visitorId,
                                ip_address: ipAddress
                            });
                        }
                        if (data.lead_id) {
                            setLeadId(data.lead_id)
                        }
                        if (data.lead_email) {
                            setEmail(data.lead_email)
                        }
                        if (data.links && links.length === 0) {
                            setLinks(data.links);
                            setIsCollapsed(false);
                        }
                        scrollToBottom();
                    } catch (error) {
                        console.error("Error parsing JSON from chunk", error);
                    }

                    // Increment the chunk counter
                    chunkCounter++;

                    // Send 'ai_responding' message every 10 chunks
                    try {
                        if (chunkCounter % 10 === 0 && socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                            socketRef.current.send(JSON.stringify({ 'type': 'ai_responding', 'responding': true }));
                        }
                    } catch (error) {
                        console.error("Error sending 'ai_responding' message", error);
                    }
                }
                // Store the remaining chunk for the next iteration
                chunkText = chunks[chunks.length - 1];
            }
        } catch (error) {
            console.error("Error reading from stream", error);
        } finally {
            const newMessage = { role: "assistant", content: receivedText, created_at: new Date().toISOString() };
            setMessages(messagesRef.current.concat(newMessage));
            if (socketRef.current) {
                socketRef.current.send(JSON.stringify({ role: "assistant", content: receivedText, created_at: new Date().toISOString(), mode: 'aimdoc' }));
            }
            // Clear the assistantResponse for the next use
            setAssistantResponse('');
            setGettingFullResponse(false);
            if (socketRef.current) {
                socketRef.current.send(JSON.stringify({ 'type': 'ai_responding', 'responding': false }))
            }
            if (config.enable_follow_ups) {
                await getFollowUps(conversationId);
            }
        }

        return conversationId;
    };

    const sendMessage = async (input: string, type?: string, firstMessage: boolean = false) => {
        if (takeOverChat && socketRef.current) {
            const message = {
                content: input,
                role: type === 'system' ? 'system' : 'user',
                created_at: new Date().toISOString(),
            };
            setShouldScroll(true);
            // Send message through WebSocket
            socketRef.current.send(JSON.stringify(message));
            setMessages((prevMessages) => [...prevMessages, message]);
        } else {
            setGettingFullResponse(true);
            if (socketRef.current) {
                socketRef.current.send(JSON.stringify({ 'type': 'ai_responding', 'responding': true }))
            }
            setFollowUps([]);
            const date = new Date().toISOString();
            const roleType = type === 'system' ? 'system' : 'user';
            if (!firstMessage) {
                setGettingResponse(true);
            }
            setMessages((prevMessages) => [...prevMessages, { role: roleType, content: input, created_at: date }]);
            if (socketRef.current) {
                socketRef.current.send(JSON.stringify({ role: roleType, content: input, created_at: date, mode: 'aimdoc' }));
            }
            setShouldScroll(true);

            const userData = {
                first_name: firstName,
                last_name: lastName,
                email: email,
                company: company,
                job_title: jobTitle
            }

            const payload: any = {
                welcome_message: { role: 'assistant', content: welcomeMessage, created_at: date },
                message: { role: roleType, content: input, created_at: date },
                user_data: userData,
                visitor_ip: ipAddress,
            };

            conLog('Payload:', payload);

            if (conversationId !== 'new' && conversationId !== "") {
                payload['conversation_id'] = conversationId;
            }

            if (leadIdRef.current !== '') {
                payload['lead_id'] = leadIdRef.current;
            }
            payload['visitor_id'] = visitorId;
            postStream(`/agents/${draftAgentId ? draftAgentId : agentId}`, payload)
                .then(async ({ stream }) => {
                    const convId = await consumeStream(stream);
                    setConversationId(convId);
                })
                .catch(error => console.error('Streaming error:', error));
        }
    };

    const getFollowUps = async (conversationId: string) => {
        post(`/ai-follow-ups/${conversationId}`, {})
            .then(({ data }) => {
                setFollowUps(JSON.parse(data).follow_ups);
                setShouldScroll(true);
            })
            .catch(error => console.error('Error getting follow ups', error));
    }

    const selectFollowUp = (followUp: string) => {
        sendMessage(followUp);
        setFollowUps([]);
        captureEvent('used_follow_up', {
            agent_id: agentId,
            message: followUp,
            ip_address: ipAddress,
        });
    }

    const checkIfMobile = () => {
        return window.innerWidth < 1025;
    };

    const checkIfLaptop = () => {
        return window.innerWidth > 1024;
    };

    const newSession = () => {
        setConversationId('');
        setMessages([]);
        setFormSubmitted(false);
        setInitMessageSent(false);
        setFollowUps([]);
    };

    useEffect(() => {
        if (!embedConfig) {
            get(`/agents/${draftAgentId ? draftAgentId : agentId}/config`)
                .then(({ data }) => {
                    setLogo(data.logo_url ? data.logo_url : undefined);
                    setConfig(data);
                })
                .catch(error => {
                    console.error('Error getting agent config:', error);
                });
        } else {
            setLogo(embedConfig.logo_url ? embedConfig.logo_url : undefined);
            setConfig(embedConfig);
        }
    }, []);

    const createLead = async () => {
        // Wait for the IP address to be fetched
        while (!ipAddress) {
            await new Promise(resolve => setTimeout(resolve, 100)); // Wait for 100ms and check again
        }

        if (!leadId && conversationId && (firstName || lastName || email || company || jobTitle)) {
            const leadData = {
                agent_id: draftAgentId ? draftAgentId : agentId,
                visitor_id: visitorId,
                first_name: firstName,
                last_name: lastName,
                email: email,
                company: company,
                job_title: jobTitle,
                attributes: attributes,
                visitor_ip: ipAddress, // Add the IP address to the lead data,
                conversation_id: conversationId,
            };

            post('/leads/', leadData)
                .then(({ data }) => {
                    setLeadId(data);
                    window.history.replaceState({}, document.title, window.location.pathname);
                })
                .catch(error => console.error('Error creating lead:', error));
        } else {
            setNoLead(true);
        }
    };

    useEffect(() => {

        if (email && formSubmitted) {
            if (socketRef.current) {
                sendMessage(`The user has submitted their information (${email}).`, 'system');

            } else {
                sendMessage(`The user has submitted their email (${email}), please inform them a representative will reach out to them shortly and ask them if there is anything else they need help with.`, 'system');
            }
            if (conversationId) {
                createLead();
            }
        }

        if (allParams['aimdoc_email'] && email && !initMessageSent && !gettingFullResponse) {
            sendMessage(`A lead named ${firstName} has landed on your website. Please greet them by their name!`, 'system');
        }

        if (!initMessageSent && !conversationId && (leadId || noLead)) {
            setInitMessageSent(true);
        }

    }, [firstName, lastName, email, company, jobTitle, ipAddress]);

    useEffect(() => {
        // Only run the effect if the necessary data is available
        if (firstName || lastName || email || company || jobTitle) {
            if (!leadId) {
                createLead();
            }
        }
    }, [conversationId]);

    useEffect(() => {
        // Fetch the IP address when the component mounts
        fetch('https://api.ipify.org?format=json')
            .then(response => response.json())
            .then(data => {
                setIpAddress(data.ip);
            })
            .catch(error => console.error('Error fetching IP address:', error));
    }, []);

    useEffect(() => {
        if (followUps.length > 0) {
            setShowFollowUps(true);  // Directly setting showQuestions to true when questions are available
        } else {
            setShowFollowUps(false); // Hide if there are no follow-up questions
        }
    }, [followUps]);

    const setMessage = (message: MessageType) => {
        setMessages((prevMessages) => {
            const messageExists = prevMessages.some(msg => msg.id === message.id);
            if (messageExists) {
                return prevMessages;
            } else {
                return [...prevMessages, message];
            }
        });
    }



    useEffect(() => {
        if (!conversationId || socketRef.current) return;

        shouldReconnectRef.current = true; // Allow reconnections

        if (conversationId && !socketRef.current) {
            const initializeWebSocket = async () => {
                try {
                    const ws = await connectWebSocket(`/ws/${conversationId}?role=user`); // Adjust the endpoint as needed
                    socketRef.current = ws;
                    // Handle incoming WebSocket messages
                    ws.onmessage = (event) => {
                        try {
                            const data = JSON.parse(event.data);
                            if (data.type === 'message' && !data.mode) {
                                if (data.role === 'assistant') {
                                    const newMessage: MessageType = {
                                        id: data.id,
                                        role: 'assistant',
                                        content: data.content,
                                        created_at: new Date().toISOString(),
                                        agent_name: data.agent_name
                                    };
                                    setMessage(newMessage);
                                }
                            }
                            if (data.type === 'take_over_chat') {
                                setAgentName(data.agent_name);
                                setShowAgentEnteredMessage(true);
                                setTakeOverChat(true);
                                setFollowUps([]);
                                const message = {
                                    ...data,
                                    "created_at": new Date().toISOString(),
                                }
                                setMessage(message);
                                const connectedTimer = setTimeout(() => {
                                    setShowConnectedMessage(true);
                                    scrollToBottom()
                                }, 3000);
                                return () => {
                                    clearTimeout(connectedTimer);
                                };
                            }
                            if (data.type === 'hand_back_chat') {
                                setTakeOverChat(false);
                                setHandBackChat(true);
                                setShowAgentLeftMessage(true);
                                setShowConnectedMessage(false);
                                const message = {
                                    ...data,
                                    "created_at": new Date().toISOString(),
                                }
                                setMessage(message);
                                const timer = setTimeout(() => {
                                    setShowAgentLeftMessage(false);
                                }, 4000);
                                return () => {
                                    clearTimeout(timer);
                                };
                            }
                            if (data.type === 'agent_closed') {
                                setTakeOverChat(false);
                                setHandBackChat(true);
                                setShowAgentLeftMessage(true);
                                setShowConnectedMessage(false);
                                const message = {
                                    ...data,
                                    "created_at": new Date().toISOString(),
                                }
                                setMessage(message);
                                const timer = setTimeout(() => {
                                    setShowAgentLeftMessage(false);
                                }, 4000);
                                return () => {
                                    clearTimeout(timer);
                                };
                            }
                            if (data.type === 'agent_typing') {
                                setIsAgentTyping(data.is_typing);
                            }
                        } catch (error) {
                            console.error('Error parsing WebSocket message:', error);
                        }
                        scrollToBottom()
                        setShouldScroll(true);
                    };

                    ws.onopen = () => {
                        console.log('WebSocket connection opened');
                        setReconnectAttempts(0);
                        ws.send(JSON.stringify({ type: 'status', status: 'active' }));
                    };

                    ws.onclose = (event) => {
                        console.log('WebSocket connection closed');
                        if (shouldReconnectRef.current) {
                            const delay = Math.min(1000 * 2 ** reconnectAttempts, 30000); // Max delay of 30 seconds
                            setTimeout(() => {
                                setReconnectAttempts((prev) => prev + 1);
                                initializeWebSocket();
                            }, delay);
                        }
                    };

                    ws.onerror = (error) => {
                        console.error('WebSocket error:', error);
                    };
                } catch (error) {
                    console.error('Failed to establish WebSocket connection:', error);
                    if (shouldReconnectRef.current) {
                        const delay = Math.min(1000 * 2 ** reconnectAttempts, 30000);
                        setTimeout(() => {
                            setReconnectAttempts((prev) => prev + 1);
                            initializeWebSocket();
                        }, delay);
                    }
                }
            };

            initializeWebSocket();
        }
        return () => {
            shouldReconnectRef.current = false; // Prevent further reconnections
            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
            }
        };
    }, [conversationId]);

    useEffect(() => {
        return () => {
            shouldReconnectRef.current = false;
            if (socketRef.current) {
                socketRef.current.close();
                socketRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (handBackChat) {
            // Use an async function to handle the message sending
            setAgentName(undefined);
            setShowConnectedMessage(false);
            const handleAgentLeft = async () => {
                try {
                    await sendMessage(`Inform the user it is you again, and ask them if there is anything else you can help them with.`, 'system');
                } catch (error) {
                    console.error("Error sending message after agent left:", error);
                }
            };
            handleAgentLeft();
            setHandBackChat(false);
        }
    }, [handBackChat]);

    console.log(config);

    return (
        messages && config && (
            <div className={`relative flex flex-col rounded-t-2xl rounded-b-lg bg-white ${agentId ? 'h-full' : 'h-screen'} w-full`}>
                <div className={`rounded-t-xl flex justify-between items-center w-full`} style={config.scheme === 'Solid' ? { backgroundColor: config.color } : { backgroundImage: `linear-gradient(to bottom right, ${config.lighter_color}, ${config.darker_color})` }}>
                    <div className={`items-center transition-all duration-200 h-14`}>
                        <img src={logo} className="mt-4 mb-4 ml-4 h-6"></img>
                    </div>
                    {agentId && (
                        <div className="flex flex-row">
                            <div onClick={() => newSession()} className={"flex items-center justify-end pr-6 hover:cursor-pointer"}>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={`w-6 h-6 stroke-white transform hover:rotate-180 duration-200`}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                                </svg>
                            </div>
                            <div onClick={() => closeModal()} className="flex items-center justify-end pr-6 hover:cursor-pointer">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6 stroke-white transform hover:rotate-180 duration-200">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex flex-row h-full">
                    <div className="flex flex-col w-full h-full">
                        {links && links.length > 0 && (
                            <div className="bg-opacity-100 z-30 w-full">
                                <div className={`flex flex-row p-2 ${links.length < 2 ? 'justify-center' : ''} overflow-x-auto scrollbar-transparent mx-4 space-x-2`}>
                                    {links.length > 0 &&
                                        links.map((link: any, index: number) => (
                                            <a
                                                key={index} href={link.url}
                                                target="_blank"
                                                className={`bg-white border rounded-lg py-1 px-2 flex flex-col flex-shrink-0 justify-between leading-normal hover:cursor-pointer transition duration-200 hover:bg-white hover:shadow-sm`}
                                                style={{
                                                    backgroundColor: `rgba(${hexToRgb(config.color)}, 0.1)`,
                                                    border: `1px solid rgba(${hexToRgb(config.color)}, 0.2)`,
                                                }}
                                            >
                                                <div className="flex flex-row items-center justify-center">
                                                    <div className="flex flex-row justify-start mr-1">
                                                        <h1
                                                            className={`text-black/70 text-sm font-semibold transition duration-200 hover:text-black hover:cursor-pointer`}
                                                            style={{
                                                                color: `rgba(${hexToRgb(config.color)}, 1)`
                                                            }}
                                                        >
                                                            {link.name}
                                                        </h1>
                                                    </div>
                                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor"
                                                        className="w-4 h-4 stroke-slate-500"
                                                        style={{
                                                            stroke: `rgba(${hexToRgb(config.color)}, 1)`
                                                        }}
                                                    >
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                                                    </svg>
                                                    {!sidebar && !checkIfMobile() && (
                                                        <div>
                                                            <p className="text-slate-500 text-sm">{link.description}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </a>
                                        ))}
                                </div>
                            </div>
                        )}
                        <div className={`transition-all duration-300 ease-in-out ${showConnectedMessage ? 'opacity-100 mb-2' : 'opacity-0 mb-0 h-0'} overflow-hidden`}>
                            <div className={`flex flex-row justify-center items-center ${links && links.length === 0 ? 'mb-2 mt-2' : 'mb-2'} animate-pulse`}>
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="size-4 fill-green-500">
                                    <path d="M8 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM12.735 14c.618 0 1.093-.561.872-1.139a6.002 6.002 0 0 0-11.215 0c-.22.578.254 1.139.872 1.139h9.47Z" />
                                </svg>

                                <div className="ml-2 flex text-xs font-medium text-green-500">
                                    <p>Connected with {agentName} (Human)</p>
                                </div>
                            </div>
                        </div>
                        {((links && links.length > 0) || takeOverChat) && (
                            <div className="border-t border-slate-100 mx-6 shadow-sm"></div>
                        )}
                        {/* <div className="flex flex-col items-center">
                            <div className={`transition-all duration-300 ease-in-out ${showAgentEnteredMessage ? 'max-h-14 opacity-100 mb-2' : 'max-h-0 opacity-0 mb-0'} overflow-hidden`}>
                                <div className="flex justify-center p-2 rounded-lg text-xs font-medium text-green-500 text-center items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 mr-2">
                                        <path fillRule="evenodd" d="M8.603 3.799A4.49 4.49 0 0 1 12 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 0 1 3.498 1.307 4.491 4.491 0 0 1 1.307 3.497A4.49 4.49 0 0 1 21.75 12a4.49 4.49 0 0 1-1.549 3.397 4.491 4.491 0 0 1-1.307 3.497 4.491 4.491 0 0 1-3.497 1.307A4.49 4.49 0 0 1 12 21.75a4.49 4.49 0 0 1-3.397-1.549 4.49 4.49 0 0 1-3.498-1.306 4.491 4.491 0 0 1-1.307-3.498A4.49 4.49 0 0 1 2.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 0 1 1.307-3.497 4.49 4.49 0 0 1 3.497-1.307Zm7.007 6.387a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                                    </svg>
                                    <p>{`${agentName} has entered the chat!`}</p>
                                </div>
                            </div>
                            <div className={`transition-all duration-300 ease-in-out ${showAgentLeftMessage ? 'max-h-20 opacity-100 mb-2' : 'max-h-0 opacity-0 mb-0'} overflow-hidden`}>
                                <div className="flex justify-center p-2 rounded-lg text-xs font-medium text-yellow-500 text-center items-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 mr-2">
                                        <path fillRule="evenodd" d="M8.603 3.799A4.49 4.49 0 0 1 12 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 0 1 3.498 1.307 4.491 4.491 0 0 1 1.307 3.497A4.49 4.49 0 0 1 21.75 12a4.49 4.49 0 0 1-1.549 3.397 4.491 4.491 0 0 1-1.307 3.497 4.491 4.491 0 0 1-3.497 1.307A4.49 4.49 0 0 1 12 21.75a4.49 4.49 0 0 1-3.397-1.549 4.49 4.49 0 0 1-3.498-1.306 4.491 4.491 0 0 1-1.307-3.498A4.49 4.49 0 0 1 2.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 0 1 1.307-3.497 4.49 4.49 0 0 1 3.497-1.307Zm7.007 6.387a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                                    </svg>
                                    <p>{`${agentName} has left the chat.`}</p>
                                </div>
                            </div>
                        </div> */}
                        <div ref={chatRef} className={`pt-2 flex flex-col w-full items-end overflow-y-auto scrollbar-thin h-full ${messages.length === 0 ? '' : 'mb-16'}`}>
                            <div className={`pl-4 pr-4 ${links.length === 0 ? 'pt-3 mt-2' : ''} w-full h-full justify-center max-w-6xl items-start`}>
                                {!allParams['first_name'] && (
                                    <Message
                                        key={welcomeMessage}
                                        role={'assistant'}
                                        content={welcomeMessage}
                                        leadEmail={email}
                                        scrollToBottom={scrollToBottom}
                                        config={{
                                            color: config.color,
                                            end_color: config.color,
                                            darker_color: config.darker_color,
                                            lighter_color: config.lighter_color,
                                            mixed_color: config.mixed_color,
                                            scheme: config.scheme,
                                            agent_message_icon_type: config.agent_message_icon_type,
                                            agent_message_icon: config.agent_message_icon,
                                            message_icon_url: config.message_icon_url,
                                        }}
                                    />
                                )}
                                {messages && messages.length === 0 && (
                                    <div className="items-center text-xs text-slate-400 mx-16">
                                        By starting this chat, you agree to our <a className="text-blue-400" target="_blank" href="https://www.iubenda.com/privacy-policy/36587222">Privacy Policy</a> and <a className="text-blue-400" target="_blank" href="https://www.iubenda.com/terms-and-conditions/36587222">Terms of Service</a>.
                                    </div>
                                )}
                                {messages
                                    .sort((a, b) => {
                                        const dateA = new Date(a.created_at.endsWith('Z') ? a.created_at : `${a.created_at}Z`);
                                        const dateB = new Date(b.created_at.endsWith('Z') ? b.created_at : `${b.created_at}Z`);
                                        return dateA.getTime() - dateB.getTime();
                                    })
                                    .map((message, index) => (
                                        config && message.role != 'system' ? (
                                            <Message
                                                key={index}
                                                role={message.role}
                                                content={message.content}
                                                created_at={message.created_at}
                                                leadEmail={email}
                                                scrollToBottom={scrollToBottom}
                                                sidebar={sidebar}
                                                agentName={message.agent_name}
                                                config={{
                                                    color: config.color,
                                                    end_color: config.color,
                                                    darker_color: config.darker_color,
                                                    lighter_color: config.lighter_color,
                                                    mixed_color: config.mixed_color,
                                                    scheme: config.scheme,
                                                    agent_message_icon_type: config.agent_message_icon_type,
                                                    agent_message_icon: config.agent_message_icon,
                                                    message_icon_url: config.message_icon_url,
                                                }}
                                            />
                                        ) : (
                                            <div className="">
                                                {message.type === 'take_over_chat' && (
                                                    <div className="flex flex-row justify-center items-center text-sm font-medium text-green-500 mt-2 mb-2">
                                                        <div className="flex mr-2 bg-green-50 items-center px-2 py-1 rounded-lg border border-green-200">
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
                                                                <path fillRule="evenodd" d="M8.603 3.799A4.49 4.49 0 0 1 12 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 0 1 3.498 1.307 4.491 4.491 0 0 1 1.307 3.497A4.49 4.49 0 0 1 21.75 12a4.49 4.49 0 0 1-1.549 3.397 4.491 4.491 0 0 1-1.307 3.497 4.491 4.491 0 0 1-3.497 1.307A4.49 4.49 0 0 1 12 21.75a4.49 4.49 0 0 1-3.397-1.549 4.49 4.49 0 0 1-3.498-1.306 4.491 4.491 0 0 1-1.307-3.498A4.49 4.49 0 0 1 2.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 0 1 1.307-3.497 4.49 4.49 0 0 1 3.497-1.307Zm7.007 6.387a.75.75 0 1 0-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 0 0-1.06 1.06l2.25 2.25a.75.75 0 0 0 1.14-.094l3.75-5.25Z" clipRule="evenodd" />
                                                            </svg>
                                                            <div className="ml-2">
                                                                {`${message.agent_name ? message.agent_name : 'Agent'} has taken over the chat.`}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                {message.type === 'hand_back_chat' && (
                                                    <div className="flex flex-row justify-center items-center text-sm font-medium text-amber-500 mt-2 mb-2">
                                                        <div className="flex mr-2 bg-amber-100 items-center px-2 py-1 rounded-lg border border-amber-200">
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
                                                                <path fillRule="evenodd" d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5ZM16.5 15a.75.75 0 0 1 .712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 0 1 0 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 0 1-1.422 0l-.395-1.183a1.5 1.5 0 0 0-.948-.948l-1.183-.395a.75.75 0 0 1 0-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0 1 16.5 15Z" clipRule="evenodd" />
                                                            </svg>

                                                            <div className="ml-2">
                                                                {`${message.agent_name ? message.agent_name : 'Agent'} has left the chat.`}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                {message.type === 'agent_closed' && (
                                                    // <div className="flex flex-row justify-center items-center text-sm font-medium text-red-500 mt-2 mb-2">
                                                    //     <div className="flex mr-2 bg-red-100 items-center px-2 py-1 rounded-lg border border-red-200">
                                                    //         <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
                                                    //             <path fillRule="evenodd" d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-1.72 6.97a.75.75 0 1 0-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 1 0 1.06 1.06L12 13.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L13.06 12l1.72-1.72a.75.75 0 1 0-1.06-1.06L12 10.94l-1.72-1.72Z" clipRule="evenodd" />
                                                    //         </svg>

                                                    //         <div className="ml-2">
                                                    //             {`${message.agent_name ? message.agent_name : 'Agent'} has disconnected.`}
                                                    //         </div>
                                                    //     </div>
                                                    // </div>
                                                    <div className="flex flex-row justify-center items-center text-sm font-medium text-amber-500 mt-2 mb-2">
                                                        <div className="flex mr-2 bg-amber-100 items-center px-2 py-1 rounded-lg border border-amber-200">
                                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-6">
                                                                <path fillRule="evenodd" d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5ZM16.5 15a.75.75 0 0 1 .712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 0 1 0 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 0 1-1.422 0l-.395-1.183a1.5 1.5 0 0 0-.948-.948l-1.183-.395a.75.75 0 0 1 0-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0 1 16.5 15Z" clipRule="evenodd" />
                                                            </svg>

                                                            <div className="ml-2">
                                                                {`${message.agent_name ? message.agent_name : 'Agent'} has left the chat.`}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    ))}
                                {(assistantResponseRef.current || gettingResponse || isAgentTyping) &&
                                    <Message
                                        role="assistant"
                                        content={assistantResponseRef.current}
                                        leadEmail={email}
                                        scrollToBottom={scrollToBottom}
                                        gettingResponse={gettingResponse}
                                        gettingFullResponse={gettingFullResponse}
                                        isAgentTyping={isAgentTyping}
                                        agentName={agentName}
                                        config={{
                                            color: config.color,
                                            end_color: config.color,
                                            darker_color: config.darker_color,
                                            lighter_color: config.lighter_color,
                                            mixed_color: config.mixed_color,
                                            scheme: config.scheme,
                                            agent_message_icon_type: config.agent_message_icon_type,
                                            agent_message_icon: config.agent_message_icon,
                                            message_icon_url: config.message_icon_url,
                                        }}
                                    />}

                                {followUps && followUps.length > 0 && (
                                    <div className={`flex flex-row gap-2 ml-2 mr-1 transition-opacity duration-[400ms] ease-in-out ${showFollowUps ? 'opacity-100' : 'opacity-0'}`}>
                                        {followUps.map((followUp: any, index: number) => (
                                            <div
                                                key={index}
                                                className={`p-2 rounded-lg mb-2 text-xs hover:cursor-pointer font-medium hover:scale-105 transition duration-150`}
                                                onClick={() => selectFollowUp(followUp)}
                                                style={{
                                                    backgroundColor: `rgba(${hexToRgb(config.color)}, 0.03)`,
                                                    border: `1px solid rgba(${hexToRgb(config.color)}, 0.1)`,
                                                    color: `rgba(${hexToRgb(config.color)}, 0.7)`
                                                }}
                                            >
                                                <p>{followUp}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {loading && <Spinner />}
                            </div>
                            <div className="flex w-full mt-4">
                                {starters && starters.length > 0 && messages.length === 0 && (
                                    <div className="flex flex-row gap-2 justify-center mx-6 w-full">
                                        {starters.map((starter: any, index: number) => (
                                            <div
                                                key={index}
                                                className="p-2 rounded-lg mb-2 text-xs hover:cursor-pointer font-medium hover:scale-105 duration-150"
                                                onClick={() => sendMessage(starter.value)}
                                                style={{
                                                    backgroundColor: `rgba(${hexToRgb(config.color)}, 0.03)`,
                                                    border: `1px solid rgba(${hexToRgb(config.color)}, 0.1)`,
                                                    color: `rgba(${hexToRgb(config.color)}, 0.7)`
                                                }}
                                            >
                                                <p>{starter.value}</p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        <ChatInput
                            sendMessage={sendMessage}
                            config={config}
                            gettingFullResponse={gettingFullResponse}
                            setEmail={setEmail}
                            setFirstName={setFirstName}
                            setLastName={setLastName}
                            setCompany={setCompany}
                            setAttributes={setAttributes}
                            setFormSubmitted={setFormSubmitted}
                            leadId={leadId} fullScreen={false}
                            socketRef={socketRef}
                            conversationId={conversationId}
                        />

                    </div>
                </div>
            </div>
        )

    );

}

export default AgentChat;